import { Popover } from "antd";
import { useCallback, useState } from "react";
import { debounce } from "throttle-debounce";
import { InputSearch } from "@/shared/Components";
import { SearchObjectTypes, useLazySearchQuery } from "@/services/api/entities/search";
import type { ChangeEvent } from "react";
import type { DebounceSearchRequest } from "./types";
import styles from "./styles.module.scss";
import { HeaderSearchOverlay } from "./HeaderSearchOverlay";
import { useOutsideClick } from "./use-click-outside";

export const HeaderSearch = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState<string>("");
  const [selectedDataTypes, setSelectedDataTypes] = useState([
    SearchObjectTypes.NOTE,
    SearchObjectTypes.PERSON,
  ]);
  const ref = useOutsideClick(() => setOpen(false));

  const [search, { data, isLoading, isError, isSuccess, reset: resetSearchData }] =
    useLazySearchQuery();

  const debounceSearch = useCallback(
    debounce(1000, (body: DebounceSearchRequest) => {
      if (body.textQuery.trim() && body.objectTypes.length) {
        search({
          maxResult: 1000,
          preHighlightTag: "--start--",
          postHighlightTag: "--end--",
          ...body,
        });
      }
    }),
    [],
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    if (!e.target.value) {
      resetSearchData();
    }
    debounceSearch({ textQuery: e.target.value, objectTypes: selectedDataTypes });
  };

  return (
    <div ref={ref}>
      <Popover
        content={
          <HeaderSearchOverlay
            textQuery={query}
            debounceSearch={debounceSearch}
            selectedDataTypes={selectedDataTypes}
            setSelectedDataTypes={setSelectedDataTypes}
            data={data}
            setOpen={setOpen}
            isLoading={isLoading}
            isError={isError}
            isSuccess={isSuccess}
            resetSearchData={resetSearchData}
          />
        }
        open={open}
        classNames={{
          body: styles.popover,
        }}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        getPopupContainer={() => ref.current}
        arrow={false}
        trigger={["click"]}
        placement={"bottomRight"}
      >
        <InputSearch onClick={() => setOpen(true)} value={query} onChange={handleChange} />
      </Popover>
    </div>
  );
};
